import * as React from 'react';

import { formatValueAsMetricNumber, MetricsGrey, MetricsNeutralColor } from '@tableau/metrics-engine';

export interface MetricValuePillProps {
  value?: number;
  measureFormat?: string;
  color?: string;
  colorIndicatorsEnabled?: boolean;
  leftAlign?: boolean;
}

/**
 * `MetricValuePill` formats the number it is given according to metrics formatting rules
 * and displays it as a "pill":
 * - if color indicators is disabled, a blue background with rounded corners, with white text.
 * - if color indicators is enabled: MetricsGrey text that is left aligned if in the list view
 *   and is centered if in the search view
 *
 * The pill will fill whatever container it is placed in.
 *
 */
export const MetricValuePill = (props: MetricValuePillProps): JSX.Element => {
  let text = formatValueAsMetricNumber(props.value, props.measureFormat);
  let style: React.CSSProperties;
  if (props.colorIndicatorsEnabled) {
    style = {
      ...colorsPillStyle,
      ...(!props.leftAlign ? { ...centeredStyle, fontWeight: 'bold' } : {}),
    };
  } else {
    style = {
      ...defaultPillStyle,
      backgroundColor: props.color ?? MetricsNeutralColor,
    };
  }

  return (
    <div style={style} title={text} data-tb-test-id={'metric-value-pill'}>
      {text}
    </div>
  );
};

const centeredStyle: React.CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingLeft: '5px',
  paddingRight: '5px',
};

const commonPillStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 'normal',
  lineHeight: 'normal',
  width: '100%',
  height: '100%',
};

const defaultPillStyle: React.CSSProperties = {
  ...commonPillStyle,
  ...centeredStyle,
  borderRadius: 2,
  color: 'white',
};

const colorsPillStyle: React.CSSProperties = {
  ...commonPillStyle,
  color: MetricsGrey,
  fontSize: 14,
};
